import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { SecondaryButton, toast } from "@jammable/ui-core";
import { checkDuration } from "@/utils/tools";
import { FaUpload } from "react-icons/fa6";
import { useModal } from "@/context/ModalContext";
import UpgradeModal from "./UpgradeModal";
import jsmediatags from "jsmediatags";
import Image from "next/image";

const UploadSongView = ({
  initialModel,
  onSongSelected,
  onError,
  onGetInTouchClick,
}) => {
  const [loading, setLoading] = useState(false);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [linkProcessingError, setLinkProcessingError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const { openModal } = useModal();
  const handleError = (err, customMessage = null, isLinkError = false) => {
    const wasYoutubeLoading = youtubeLoading;
    setLoading(false);
    setYoutubeLoading(false);
    setProgress(0);
    const errorMessage =
      customMessage ||
      err?.response?.data?.message ||
      "An unexpected error occurred.";

    if (errorMessage === "You are out of credits") {
      openModal(
        "upgrade-modal",
        <UpgradeModal
          from="upload-song-view"
          dailyLimit={false}
          message="You are out of credits"
        />,
        { className: "tw-p-0" },
      );

      if (onError) {
        onError(errorMessage, err);
      }
    } else {
      if (isLinkError || wasYoutubeLoading) {
        setLinkProcessingError(errorMessage);
      } else {
        toast.error(errorMessage);
      }

      if (onError) {
        onError(errorMessage, err);
      }
    }
  };

  const getPresignedUrl = async (token) => {
    try {
      const response = await axios.get("/api/s3/modelS3PresignedUrl", {
        params: { model: initialModel?.urlParam },
        headers: { Authorization: token },
      });
      return {
        url: response.data.url,
        key: response.data.key,
        absoluteUrl: response.data.absoluteUrl,
      };
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  const handleUpload = async (key, file, url, token, absoluteUrl, metadata) => {
    const options = {
      headers: {
        "Content-Type": file.type,
      },
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        const progress = Math.round((loaded / total) * 99);
        setProgress(progress);
      },
    };
    try {
      await axios.put(url, file, options);
      setProgress(100);
      if (onSongSelected) {
        onSongSelected({
          url: absoluteUrl,
          title: metadata?.title || file.name.replace(/\\.[^/.]+$/, ""),
          metadata,
        });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleFileUpload = useCallback(
    async (acceptedFiles) => {
      setProgress(0);
      if (acceptedFiles?.length > 0) {
        setLoading(true);
        const file = acceptedFiles[0];

        if (file.size > 75 * 1024 * 1024) {
          handleError(
            null,
            "File size exceeds 75MB! Please upload a smaller file.",
          );
          setLoading(false);
          return;
        }

        try {
          const duration = await checkDuration(file);
          if (duration && duration > 6 * 60) {
            handleError(
              null,
              "Audio duration exceeds 6 minutes! Please upload a shorter file.",
            );
            setLoading(false);
            return;
          }

          const currentMetadata = await new Promise((resolve) => {
            jsmediatags.read(file, {
              onSuccess: (tag) => {
                const { title, artist, album, picture } = tag.tags;
                let image = null;
                if (picture) {
                  const { data, format } = picture;
                  const blob = new Blob([new Uint8Array(data)], {
                    type: format,
                  });
                  const url = URL.createObjectURL(blob);
                  image = { url };
                }
                resolve({ title, artist, album, image });
              },
              onError: (error) => {
                console.warn("Could not read metadata", error);
                resolve({});
              },
            });
          });

          setMetadata(currentMetadata);

          const auth = getAuth();
          const token = await auth.currentUser.getIdToken();
          const { key, url, absoluteUrl } = await getPresignedUrl(token);
          await handleUpload(
            key,
            file,
            url,
            token,
            absoluteUrl,
            currentMetadata,
          );
        } catch (err) {
          console.error("File upload process failed:", err);
        } finally {
          setLoading(false);
        }
      } else {
        toast.error("Please upload a valid audio file");
      }
    },
    [initialModel, onSongSelected, onError],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileUpload,
    maxFiles: 1,
    accept: {
      "audio/*": [".wav", ".mp3", ".m4a"],
    },
    disabled: loading || youtubeLoading,
    onClick: () => {
      setLinkProcessingError(null);
      setMetadata(null);
    },
  });

  return (
    <section className="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-outline-none">
      <div className="tw-w-full  tw-flex tw-flex-col tw-gap-4">
        <div
          {...getRootProps()}
          className={`tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-6 tw-py-10 tw-border-dashed tw-border-2 ${linkProcessingError ? "tw-border-red-300" : "tw-border-gray-300 hover:tw-border-purple-300"} tw-rounded-3xl tw-outline-none tw-cursor-pointer tw-transition tw-bg-gray-50 hover:tw-bg-gray-100`}
        >
          <input {...getInputProps()} />
          {loading ? (
            <div className="tw-p-4 tw-flex tw-flex-col tw-items-center">
              <div className="tw-flex tw-gap-4 tw-items-center tw-w-full">
                {metadata?.image?.url && (
                  <Image
                    src={metadata.image.url}
                    alt="cover"
                    width={80}
                    height={80}
                    className="tw-rounded-lg"
                  />
                )}
                <div className="tw-flex tw-flex-col tw-text-left tw-truncate tw-w-full">
                  <div className="tw-font-bold tw-truncate">
                    {metadata?.title || "Uploading..."}
                  </div>
                  <div className="tw-text-sm tw-truncate">
                    {metadata?.artist}
                  </div>
                </div>
              </div>
              <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2.5 tw-mt-4">
                <div
                  className="tw-bg-purple-600 tw-h-2.5 tw-rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="tw-mt-2 tw-text-md tw-text-purple-600 tw-font-black">
                {progress}%
              </div>
            </div>
          ) : (
            <>
              <FaUpload className={`tw-mb-3 tw-size-8 tw-text-purple-500`} />
              <div className="tw-font-bold tw-text-md tw-text-purple-700 tw-text-center">
                Drop an audio file or click to browse
              </div>
              <div className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-text-center">
                Max 75MB, 6 mins • MP3, WAV, M4A
              </div>
            </>
          )}
        </div>
        {/* <div className=" tw-w-full tw-rounded-3xl tw-bg-gradient-to-br tw-from-purple-100 tw-to-indigo-100 tw-p-4 tw-shadow-sm tw-transition-shadow tw-duration-200">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <div className="tw-flex-1 tw-w-full tw-text-wrap">
              <div className="tw-font-bold tw-text-sm tw-text-gray-900">
                Want to upload to the Jammable library?
              </div>
              <div className="tw-text-xs tw-text-black/50 tw-mt-0.5">
                Share <span className="tw-font-bold">your</span> unique sounds
                with the community!
              </div>
            </div>
            <SecondaryButton
              onClick={onGetInTouchClick}
              className="tw-cursor-pointer tw-font-black tw-tracking-wider tw-px-3 tw-py-1.5  tw-rounded-md  tw-rounded-full"
            >
              UPLOAD
            </SecondaryButton>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default UploadSongView;
